/**
 * Build a single ordered gallery list for the property page hero + photo modal:
 * stay.images → paw story reel → founderReviews images → each room's images → highlights (features) images.
 * De-duplicates by URL (ignoring query string). Image-only helpers skip video URLs.
 */

import { collectStayGalleryMedia, galleryMediaToUrls, isGalleryVideoUrl } from './galleryMedia';

function isLikelyImageUrl(url) {
  if (typeof url !== 'string') return false;
  const u = url.trim();
  if (!u) return false;
  if (isGalleryVideoUrl(u)) return false;
  return true;
}

function dedupeKey(url) {
  try {
    const u = new URL(url.trim());
    return `${u.origin}${u.pathname}`;
  } catch {
    return url.trim().split('?')[0];
  }
}

/**
 * @param {object|null|undefined} stay
 * @returns {string[]}
 */
export function collectPropertyGalleryImageUrls(stay) {
  return galleryMediaToUrls(collectStayGalleryMedia(stay).filter((item) => item.type === 'image'));
}

export { collectStayGalleryMedia, galleryMediaToUrls } from './galleryMedia';

/**
 * Stay-level photos for room cards when a room has no images (excludes other rooms' photos).
 * @param {object|null|undefined} stay
 * @returns {string[]}
 */
export function collectStayGalleryImageUrlsForRoomFallback(stay) {
  if (!stay || typeof stay !== 'object') return [];

  const seen = new Set();
  const out = [];

  const push = (url) => {
    if (!isLikelyImageUrl(url)) return;
    const u = url.trim();
    const key = dedupeKey(u);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(u);
  };

  (Array.isArray(stay.images) ? stay.images : []).forEach(push);

  (Array.isArray(stay.founderReviews) ? stay.founderReviews : []).forEach((r) => {
    if (r && r.image) push(r.image);
  });

  (Array.isArray(stay.features) ? stay.features : []).forEach((f) => {
    if (f && f.image) push(f.image);
  });

  return out;
}

const DEFAULT_ROOM_IMAGE =
  'https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=400&h=300&fit=crop';

/**
 * Room carousel URLs: room.images, else stay gallery, else placeholder.
 * @param {object} room
 * @param {object|null} stay
 * @returns {string[]}
 */
export function resolveRoomDisplayImages(room, stay = null) {
  const roomImgs = (Array.isArray(room?.images) ? room.images : []).filter(isLikelyImageUrl);
  if (roomImgs.length) return roomImgs.map((u) => u.trim());

  const stayGallery = collectStayGalleryImageUrlsForRoomFallback(stay);
  if (stayGallery.length) return stayGallery;

  return [DEFAULT_ROOM_IMAGE];
}
